import React, { useContext, useEffect, useMemo, useState } from "react";
import { injectIntl } from "react-intl";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Table,
  UncontrolledDropdown
} from "reactstrap";
import ReactTable from "react-table";
import "react-table/react-table.css";
import IntlMessages from "../../../helpers/IntlMessages";
import ShopeeV2Api from "../../../services/shopeeV2";
import ShopeeListingEditor from "./ShopeeListingEditor";
import { isConcreteShopeeShopId } from "./shopeeListingEditorData";
import { mergeShopeeCatalogFilters } from "./shopeeCatalogFilters";
import {
  getShopeeMappingStatus,
  mappingHasSyncError,
  shopeeMappingStatusLabel
} from "./shopeeMappingStatus";
import ShopeeOrdersPanel from "./ShopeeOrdersPanel";
import ShopeeOverviewPanel from "./ShopeeOverviewPanel";
import ShopeeWorkspaceContext from "./ShopeeWorkspaceContext";
import ShopeeFinancePanel from "./ShopeeFinancePanel";
import "./ShopeeCatalog.scss";

const T = ({ id }) => <IntlMessages id={id} />;
const money = value => Number(value || 0).toLocaleString("vi-VN");
const canWrite = (capabilities, alias) =>
  capabilities[alias] && capabilities[alias].write;

const Empty = () => (
  <div className="text-center text-muted py-5">
    <T id="shopee.workspace.no-data" />
  </div>
);

const Status = ({ value }) => {
  const text = String(value || "unknown");
  const color = /completed|healthy|mapped|released|paid/i.test(text)
    ? "success"
    : /failed|dead|blocked|expired|cancel/i.test(text)
      ? "danger"
      : "warning";
  return <Badge color={color}>{text}</Badge>;
};

const TableShell = ({ columns, rows, rowKey, actions }) => {
  if (!rows || !rows.length) return <Empty />;
  return (
    <div className="table-responsive">
      <Table hover className="mb-0">
        <thead>
          <tr>
            {columns.map(column => <th key={column.key}>{column.label}</th>)}
            {actions ? <th className="text-right"><T id="shopee.workspace.actions" /></th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row[rowKey] || row.id}>
              {columns.map(column => (
                <td key={column.key}>
                  {column.render ? column.render(row[column.key], row) : row[column.key]}
                </td>
              ))}
              {actions ? <td className="text-right text-nowrap">{actions(row)}</td> : null}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export function OverviewPanel() {
  return <ShopeeOverviewPanel />;
}

export function OrdersPanel() {
  return <ShopeeOrdersPanel />;
}

const CATALOG_CHANNELS = [
  {
    key: "all",
    labelId: "shopee.workspace.catalog.channel-all",
    mark: "#",
    tone: "all"
  },
  { key: "shopee", label: "Shopee", mark: "S", tone: "shopee" },
  { key: "tiktok", label: "TikTok Shop", mark: "♪", tone: "tiktok" },
  { key: "lazada", label: "Lazada", mark: "L", tone: "lazada" },
  { key: "tiki", label: "Tiki", mark: "Ti", tone: "tiki" }
];

const relativeTime = (value, intl) => {
  const notSynced =
    intl &&
    intl.formatMessage({ id: "shopee.workspace.catalog.not-synced-yet" });
  if (!value) return notSynced || "Chưa đồng bộ";
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return notSynced || "Chưa đồng bộ";
  const minutes = Math.max(1, Math.round((Date.now() - timestamp) / 60000));
  if (minutes < 60)
    return (
      intl &&
      intl.formatMessage(
        { id: "shopee.workspace.catalog.minutes-ago" },
        { count: minutes }
      )
    ) || `${minutes} phút trước`;
  const hours = Math.round(minutes / 60);
  if (hours < 24)
    return (
      intl &&
      intl.formatMessage(
        { id: "shopee.workspace.catalog.hours-ago" },
        { count: hours }
      )
    ) || `${hours} giờ trước`;
  return new Date(value).toLocaleDateString("vi-VN");
};

const CatalogProductThumb = ({ row }) => (
  <span className="shopee-catalog__thumb">
    {row.vmass_image || row.platform_image ? (
      <img src={row.vmass_image || row.platform_image} alt="" />
    ) : (
      <i className="iconsminds-box-full" aria-hidden="true" />
    )}
  </span>
);

const ChannelState = ({ active, error, stock, price }) => {
  if (!active) {
    return (
      <div className="shopee-catalog__channel-state is-muted">
        <span>
          <i className="simple-icon-close" />{" "}
          <T id="shopee.workspace.catalog.channel-unlisted" />
        </span>
        <small>
          + <T id="shopee.workspace.catalog.publish-now" />
        </small>
      </div>
    );
  }
  const outOfStock = Number(stock || 0) <= 0;
  return (
    <div className={`shopee-catalog__channel-state ${error ? "is-error" : outOfStock ? "is-warning" : "is-success"}`}>
      <span>
        <i className={error ? "simple-icon-exclamation" : outOfStock ? "simple-icon-clock" : "simple-icon-check"} />
        {error ? (
          <T id="shopee.workspace.catalog.sync-error-short" />
        ) : outOfStock ? (
          <T id="shopee.workspace.catalog.out-of-stock" />
        ) : (
          <T id="shopee.workspace.catalog.listed" />
        )}
      </span>
      <small>
        {error ? (
          <T id="shopee.workspace.catalog.view-error" />
        ) : (
          `${money(price)} đ`
        )}
      </small>
    </div>
  );
};

export const CatalogPanel = injectIntl(function CatalogPanel({
  intl,
  inventoryOnly = false
}) {
  const workspace = useContext(ShopeeWorkspaceContext);
  const alias = inventoryOnly ? "shopee_inventory" : "shopee_catalog";
  const writable = canWrite(workspace.capabilities, alias);
  const [mappingDialog, setMappingDialog] = useState(null);
  const [productId, setProductId] = useState("");
  const [variantId, setVariantId] = useState("");
  const [candidateSearch, setCandidateSearch] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [listingEditor, setListingEditor] = useState(null);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [mappingFilter, setMappingFilter] = useState("all");
  const [syncFilter, setSyncFilter] = useState("all");
  const [activeChannel, setActiveChannel] = useState("all");
  const [detailRow, setDetailRow] = useState(null);
  const hasConcreteShop = isConcreteShopeeShopId(workspace.selectedShopId);
  const setWorkspaceFilters = workspace.setFilters;
  const openListingEditor = editor => setListingEditor(editor);
  const openMapping = row => {
    setMappingDialog(row);
    setProductId(row.product_id || "");
    setVariantId(row.variant_id || "");
    setCandidateSearch("");
  };
  useEffect(() => {
    if (!mappingDialog) return undefined;
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setCandidatesLoading(true);
      try {
        const response = await ShopeeV2Api.catalogCandidates(
          { search: candidateSearch, page_size: 100 },
          controller.signal
        );
        setCandidates((response && response.data) || []);
      } catch (error) {
        if (error.name !== "CanceledError" && error.code !== "ERR_CANCELED") {
          setCandidates([]);
        }
      } finally {
        if (!controller.signal.aborted) setCandidatesLoading(false);
      }
    }, 250);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [mappingDialog, candidateSearch]);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setWorkspaceFilters(current =>
        mergeShopeeCatalogFilters(current, {
          search: catalogSearch,
          mappingState: mappingFilter,
          syncState: syncFilter
        })
      );
    }, 250);
    return () => window.clearTimeout(timer);
  }, [catalogSearch, mappingFilter, setWorkspaceFilters, syncFilter]);
  const selectedCandidate = candidates.find(
    candidate => String(candidate.id) === String(productId)
  );
  const availableVariants =
    (selectedCandidate && selectedCandidate.variants) || [];
  const requiresVariant =
    mappingDialog && String(mappingDialog.platform_model_id || "0") !== "0";
  const saveMapping = () => {
    if (
      !mappingDialog ||
      !Number(productId) ||
      (requiresVariant && !Number(variantId))
    ) {
      return;
    }
    workspace.runAction(
      `mapping-${mappingDialog.id}`,
      key =>
        ShopeeV2Api.updateMapping(
          mappingDialog.id,
          {
            product_id: Number(productId),
            variant_id: variantId ? Number(variantId) : null
          },
          key
        ),
      intl.formatMessage({ id: "shopee.workspace.catalog.mapping-saved" })
    );
    setMappingDialog(null);
  };
  const clearMapping = () => {
    if (!mappingDialog) return;
    workspace.runAction(
      `mapping-${mappingDialog.id}`,
      key =>
        ShopeeV2Api.updateMapping(
          mappingDialog.id,
          { product_id: null, variant_id: null },
          key
        ),
      intl.formatMessage({
        id: "shopee.workspace.catalog.mapping-cleared"
      })
    );
    setMappingDialog(null);
  };
  const action = inventoryOnly
    ? () => workspace.runAction(
        "sync-inventory",
        key => ShopeeV2Api.syncInventory(workspace.selectedShopId, null, key),
        intl.formatMessage({
          id: "shopee.workspace.catalog.inventory-sync-queued"
        })
      )
    : () => workspace.runAction(
        "import-catalog",
        key => ShopeeV2Api.importCatalog(workspace.selectedShopId, null, key),
        intl.formatMessage({
          id: "shopee.workspace.catalog.import-queued"
        })
      );
  const mappings = useMemo(
    () => workspace.data.mappings || [],
    [workspace.data.mappings]
  );
  const filteredMappings = useMemo(
    () => ['all', 'shopee'].includes(activeChannel) ? mappings : [],
    [activeChannel, mappings]
  );
  const mappedCount = mappings.filter(row => row.product_id).length;
  const unmappedCount = mappings.length - mappedCount;
  const errorCount = mappings.filter(mappingHasSyncError).length;
  const outOfStockCount = mappings.filter(row => Number(row.platform_stock || 0) <= 0).length;
  const pendingPriceCount = mappings.filter(row =>
    /price/i.test(String(row.conflict_state || ""))).length;
  const totalMappings = Number(
    workspace.meta.total === undefined ? mappings.length : workspace.meta.total
  );
  const totalProducts = Number(
    workspace.meta.total_items === undefined
      ? totalMappings
      : workspace.meta.total_items
  );
  const page = Number(workspace.meta.page || workspace.filters.page || 1);
  const totalPages = Math.max(1, Number(workspace.meta.total_pages || 1));
  const pageSize = Number(workspace.meta.page_size || workspace.filters.page_size || 20);
  const selection = workspace.selection || [];
  const visibleIds = filteredMappings.map(row => row.id);
  const allVisibleSelected = Boolean(visibleIds.length) &&
    visibleIds.every(id => selection.includes(id));
  const updatePage = nextPage => workspace.setFilters({
    ...workspace.filters,
    page: Math.min(totalPages, Math.max(1, nextPage))
  });
  const toggleRow = id => workspace.setSelection(
    selection.includes(id)
      ? selection.filter(selectedId => selectedId !== id)
      : [...selection, id]
  );
  const toggleVisibleRows = () => workspace.setSelection(
    allVisibleSelected
      ? selection.filter(id => !visibleIds.includes(id))
      : Array.from(new Set([...selection, ...visibleIds]))
  );
  const kpis = [
    {
      label: intl.formatMessage({ id: "shopee.workspace.catalog.kpi-total-listings" }),
      value: totalProducts,
      note: intl.formatMessage(
        { id: "shopee.workspace.catalog.kpi-total-listings-note" },
        { count: totalMappings }
      ),
      icon: "iconsminds-box-full",
      tone: "blue"
    },
    {
      label: intl.formatMessage({ id: "shopee.workspace.catalog.kpi-mapped" }),
      value: mappedCount,
      note: intl.formatMessage(
        { id: "shopee.workspace.catalog.kpi-page-percent" },
        {
          percent: mappings.length
            ? Math.round((mappedCount / mappings.length) * 100)
            : 0
        }
      ),
      icon: "simple-icon-check",
      tone: "green"
    },
    {
      label: intl.formatMessage({ id: "shopee.workspace.catalog.kpi-unmapped" }),
      value: unmappedCount,
      note: intl.formatMessage({ id: "shopee.workspace.catalog.note-needs-action" }),
      icon: "simple-icon-link",
      tone: "orange"
    },
    {
      label: intl.formatMessage({ id: "shopee.workspace.catalog.kpi-sync-errors" }),
      value: errorCount,
      note: intl.formatMessage({ id: "shopee.workspace.catalog.note-priority" }),
      icon: "simple-icon-exclamation",
      tone: "red"
    },
    {
      label: intl.formatMessage({ id: "shopee.workspace.catalog.kpi-out-of-stock" }),
      value: outOfStockCount,
      note: intl.formatMessage({ id: "shopee.workspace.catalog.note-at-shopee" }),
      icon: "iconsminds-empty-box",
      tone: "purple"
    },
    {
      label: intl.formatMessage({ id: "shopee.workspace.catalog.kpi-price-pending" }),
      value: pendingPriceCount,
      note: intl.formatMessage({ id: "shopee.workspace.catalog.note-sync-price" }),
      icon: "simple-icon-refresh",
      tone: "cyan"
    }
  ];
  if (!inventoryOnly) return (
    <React.Fragment>
      <div className={`shopee-catalog ${detailRow ? "has-detail" : ""}`}>
        <main className="shopee-catalog__main">
          <div className="shopee-catalog__kpis">
            {kpis.map(kpi => (
              <section className="shopee-catalog__kpi" key={kpi.label}>
                <span className={`shopee-catalog__kpi-icon is-${kpi.tone}`}>
                  <i className={kpi.icon} />
                </span>
                <div>
                  <span>{kpi.label}</span>
                  <strong>{money(kpi.value)}</strong>
                  <small>{kpi.note}</small>
                </div>
              </section>
            ))}
          </div>

          <section
            className="shopee-catalog__toolbar"
            aria-label={intl.formatMessage({
              id: "shopee.workspace.catalog.filter-aria"
            })}
          >
            <label className="shopee-catalog__search">
              <i className="simple-icon-magnifier" />
              <Input
                value={catalogSearch}
                onChange={event => setCatalogSearch(event.target.value)}
                placeholder={intl.formatMessage({
                  id: "shopee.workspace.catalog.search-placeholder"
                })}
              />
            </label>
            <Input type="select" aria-label={intl.formatMessage({ id: "shopee.workspace.catalog.channel-aria" })} value={activeChannel} onChange={event => setActiveChannel(event.target.value)}>
              {CATALOG_CHANNELS.map(channel => (
                <option key={channel.key} value={channel.key}>
                  {channel.labelId ? (
                    <T id={channel.labelId} />
                  ) : (
                    channel.label
                  )}
                </option>
              ))}
            </Input>
            <Input type="select" aria-label={intl.formatMessage({ id: "shopee.workspace.catalog.mapping-state-aria" })} value={mappingFilter} onChange={event => setMappingFilter(event.target.value)}>
              <option value="all"><T id="shopee.workspace.catalog.mapping-all" /></option>
              <option value="mapped"><T id="shopee.workspace.catalog.mapping-mapped" /></option>
              <option value="unmapped"><T id="shopee.workspace.catalog.mapping-unmapped" /></option>
            </Input>
            <Input type="select" aria-label={intl.formatMessage({ id: "shopee.workspace.catalog.sync-state-aria" })} value={syncFilter} onChange={event => setSyncFilter(event.target.value)}>
              <option value="all"><T id="shopee.workspace.catalog.sync-state-all" /></option>
              <option value="healthy">Healthy</option>
              <option value="error"><T id="shopee.workspace.catalog.sync-state-error" /></option>
            </Input>
            {writable ? (
              <div className="shopee-catalog__toolbar-actions">
                <Button color="light" onClick={action} disabled={workspace.busy["import-catalog"]}>
                  <i className="simple-icon-refresh mr-2" />{" "}
                  {workspace.busy["import-catalog"] ? (
                    <T id="shopee.workspace.common.sending-request" />
                  ) : (
                    <T id="shopee.workspace.common.sync" />
                  )}
                </Button>
                <Button
                  color="primary"
                  onClick={() => openListingEditor({ mode: "publish" })}
                >
                  <i className="simple-icon-plus" />{" "}
                  <T id="shopee.workspace.catalog.add-product" />
                </Button>
              </div>
            ) : null}
          </section>

          <Card className="shopee-catalog__table-card">
            <div className="shopee-catalog__channel-tabs">
              <div>
                {CATALOG_CHANNELS.map(channel => (
                  <button
                    type="button"
                    className={activeChannel === channel.key ? "is-active" : ""}
                    key={channel.key}
                    onClick={() => setActiveChannel(channel.key)}
                  >
                    <span className={`channel-mark is-${channel.tone}`}>{channel.mark}</span>
                    {channel.label}
                    <small>{channel.key === "all" ? totalProducts : channel.key === "shopee" ? totalProducts : 0}</small>
                  </button>
                ))}
              </div>
              <Button color="light" size="sm"><i className="simple-icon-grid" /> Matrix view</Button>
            </div>

            <div className="table-responsive shopee-catalog__table-wrap">
              <Table hover className="shopee-catalog__table mb-0">
                <thead>
                  <tr>
                    <th className="shopee-catalog__selection-cell"><Input type="checkbox" className="shopee-catalog__selection-control" checked={allVisibleSelected} onChange={toggleVisibleRows} aria-label={intl.formatMessage({ id: "shopee.workspace.catalog.select-all-aria" })} /></th>
                    <th><T id="shopee.workspace.catalog.col-product" /> <small>(<T id="shopee.workspace.catalog.col-sku-vmass" />)</small></th>
                    <th><T id="shopee.workspace.catalog.col-price" /> <small>(VMASS)</small></th>
                    <th><T id="shopee.workspace.catalog.col-stock" /> <small>(VMASS)</small></th>
                    <th><span className="channel-mark is-shopee">S</span> Shopee</th>
                    <th><span className="channel-mark is-tiktok">♪</span> TikTok Shop</th>
                    <th><span className="channel-mark is-lazada">L</span> Lazada</th>
                    <th><span className="channel-mark is-tiki">Ti</span> Tiki</th>
                    <th><T id="shopee.workspace.catalog.col-sync-state" /></th>
                    <th><T id="shopee.workspace.catalog.col-last-update" /></th>
                    <th className="text-center"><T id="shopee.workspace.actions" /></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMappings.map(row => {
                    const mappingStatus = getShopeeMappingStatus(row);
                    const hasError = mappingStatus === "error";
                    const healthy = mappingStatus === "healthy";
                    return (
                      <tr key={row.id} className={detailRow && detailRow.id === row.id ? "is-selected" : ""} onClick={() => setDetailRow(row)}>
                        <td className="shopee-catalog__selection-cell" onClick={event => event.stopPropagation()}>
                          <Input type="checkbox" className="shopee-catalog__selection-control" checked={selection.includes(row.id)} onChange={() => toggleRow(row.id)} aria-label={intl.formatMessage({ id: "shopee.workspace.catalog.select-row-aria" }, { name: row.platform_name || row.platform_sku })} />
                        </td>
                        <td>
                          <div className="shopee-catalog__product">
                            <CatalogProductThumb row={row} />
                            <div>
                              <strong>
                                {row.vmass_product_name || (
                                  <T id="shopee.workspace.catalog.no-vmass-mapping" />
                                )}
                              </strong>
                              <small>
                                {row.vmass_sku || (
                                  <T id="shopee.workspace.catalog.no-vmass-sku" />
                                )}
                              </small>
                            </div>
                          </div>
                        </td>
                        <td className="font-weight-medium">{row.vmass_price == null ? "—" : `${money(row.vmass_price)} đ`}</td>
                        <td className={row.vmass_stock != null && Number(row.vmass_stock) <= 0 ? "text-danger font-weight-bold" : "font-weight-bold"}>{row.vmass_stock == null ? "—" : money(row.vmass_stock)}</td>
                        <td><ChannelState active error={hasError} stock={row.platform_stock} price={row.platform_price} /></td>
                        <td><ChannelState /></td>
                        <td><ChannelState /></td>
                        <td><ChannelState /></td>
                        <td><span className={`shopee-catalog__sync-state is-${healthy ? "healthy" : hasError ? "error" : "warning"}`}><i /><IntlMessages id={shopeeMappingStatusLabel(mappingStatus)} /></span></td>
                        <td>{relativeTime(row.last_synced_at || row.updatedAt, intl)}</td>
                        <td className="text-center" onClick={event => event.stopPropagation()}>
                          <Button color="light" size="sm" className="shopee-catalog__more" onClick={() => setDetailRow(row)} aria-label={intl.formatMessage({ id: "shopee.workspace.catalog.open-detail-aria" })}><i className="simple-icon-options" /></Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              {!filteredMappings.length ? <Empty /> : null}
            </div>

            <footer className="shopee-catalog__pagination">
              <span>
                <T
                  id="shopee.workspace.catalog.pagination-summary"
                  values={{
                    from: filteredMappings.length ? ((page - 1) * pageSize) + 1 : 0,
                    to: ((page - 1) * pageSize) + filteredMappings.length,
                    total: money(totalMappings)
                  }}
                />
              </span>
              <div>
                <Button color="light" size="sm" disabled={page <= 1} onClick={() => updatePage(page - 1)}>‹</Button>
                <Button color="primary" size="sm">{page}</Button>
                {page < totalPages ? <Button color="light" size="sm" onClick={() => updatePage(page + 1)}>{page + 1}</Button> : null}
                {page + 1 < totalPages ? <span>…</span> : null}
                {page + 1 < totalPages ? <Button color="light" size="sm" onClick={() => updatePage(totalPages)}>{totalPages}</Button> : null}
                <Button color="light" size="sm" disabled={page >= totalPages} onClick={() => updatePage(page + 1)}>›</Button>
              </div>
              <Input type="select" value={pageSize} onChange={event => workspace.setFilters({ ...workspace.filters, page: 1, page_size: Number(event.target.value) })}>
                {[20, 50, 100].map(size => (
                  <option value={size} key={size}>
                    <T
                      id="shopee.workspace.common.per-page"
                      values={{ size }}
                    />
                  </option>
                ))}
              </Input>
            </footer>
          </Card>
        </main>

        {detailRow ? (
          <aside className="shopee-catalog__detail">
            <header>
              <div>
                <strong>
                  {detailRow.vmass_product_name || (
                    <T id="shopee.workspace.catalog.no-vmass-mapping" />
                  )}
                </strong>
                <small>
                  <T id="shopee.workspace.catalog.detail-sku-label" />{" "}
                  {detailRow.vmass_sku || "—"} |{" "}
                  <T id="shopee.workspace.catalog.detail-item-label" />{" "}
                  {detailRow.platform_item_id}
                </small>
              </div>
              <button type="button" onClick={() => setDetailRow(null)} aria-label={intl.formatMessage({ id: "shopee.workspace.catalog.close-detail-aria" })}><i className="simple-icon-close" /></button>
            </header>
            <div className="shopee-catalog__detail-product">
              <CatalogProductThumb row={detailRow} />
              {writable ? <Button size="sm" color="light" onClick={() => openListingEditor({ mode: "update", mapping: detailRow })}><i className="simple-icon-pencil" /> <T id="shopee.workspace.common.edit" /></Button> : null}
            </div>
            <nav>
              <button type="button" className="is-active"><T id="shopee.workspace.tab.connections-short" /></button>
              <button type="button"><T id="shopee.workspace.catalog.nav-stock" /></button>
              <button type="button"><T id="shopee.workspace.catalog.nav-history" /></button>
            </nav>
            <section className="shopee-catalog__detail-channel">
              <div className="shopee-catalog__detail-channel-title"><span><span className="channel-mark is-shopee">S</span> Shopee</span><Badge color={detailRow.product_id ? "success" : "warning"}>{detailRow.product_id ? <T id="shopee.workspace.catalog.listed" /> : <T id="shopee.workspace.catalog.unmapped-badge" />}</Badge></div>
              <dl>
                <div><dt><T id="shopee.workspace.common.product-name" /></dt><dd>{detailRow.platform_name || "—"}</dd></div>
                <div><dt><T id="shopee.workspace.catalog.sku-shopee" /></dt><dd>{detailRow.platform_sku || "—"}</dd></div>
                <div><dt><T id="shopee.workspace.catalog.col-price" /></dt><dd>{money(detailRow.platform_price)} đ</dd></div>
                <div><dt><T id="shopee.workspace.catalog.col-stock" /></dt><dd>{money(detailRow.platform_stock)}</dd></div>
              </dl>
              {writable ? <Button block color={detailRow.product_id ? "outline-primary" : "primary"} onClick={() => openMapping(detailRow)}>{detailRow.product_id ? <T id="shopee.workspace.catalog.update-mapping" /> : <T id="shopee.workspace.catalog.map-product-btn" />}</Button> : null}
            </section>
            {["TikTok Shop", "Lazada", "Tiki"].map((channel, index) => (
              <section className="shopee-catalog__detail-channel is-unlisted" key={channel}>
                <div className="shopee-catalog__detail-channel-title"><span><span className={`channel-mark is-${index === 0 ? "tiktok" : index === 1 ? "lazada" : "tiki"}`}>{index === 0 ? "♪" : index === 1 ? "L" : "Ti"}</span> {channel}</span><Badge color="warning"><T id="shopee.workspace.catalog.channel-unlisted" /></Badge></div>
                <p><T id="shopee.workspace.catalog.no-channel-info" /></p>
                <Button block color="light" disabled><T id="shopee.workspace.catalog.publish-to" values={{ channel }} /></Button>
              </section>
            ))}
            {writable ? <Button className="shopee-catalog__sync-all" block color="primary" onClick={action} disabled={workspace.busy["import-catalog"]}><i className="simple-icon-refresh mr-2" /> {workspace.busy["import-catalog"] ? <T id="shopee.workspace.common.sending-request" /> : <T id="shopee.workspace.catalog.sync-shopee-catalog" />}</Button> : null}
          </aside>
        ) : null}
      </div>

      <Modal isOpen={Boolean(mappingDialog)} toggle={() => setMappingDialog(null)}>
        <ModalHeader toggle={() => setMappingDialog(null)}><T id="shopee.workspace.mapping-wizard" /></ModalHeader>
        <ModalBody>
          {mappingDialog ? <Alert color="info">{mappingDialog.platform_name || mappingDialog.platform_sku} — Model {mappingDialog.platform_model_id || "0"}</Alert> : null}
          <FormGroup><Label><T id="shopee.workspace.search-product" /></Label><Input value={candidateSearch} onChange={event => setCandidateSearch(event.target.value)} placeholder={intl.formatMessage({ id: "shopee.workspace.catalog.sku-name-placeholder" })} /></FormGroup>
          <FormGroup>
            <Label><T id="shopee.workspace.choose-product" /></Label>
            <Input type="select" value={productId} disabled={candidatesLoading} onChange={event => { setProductId(event.target.value); setVariantId(""); }}>
              <option value="">—</option>
              {candidates.map(candidate => <option key={candidate.id} value={candidate.id}>{candidate.name} ({candidate.sku})</option>)}
            </Input>
          </FormGroup>
          {requiresVariant ? <FormGroup><Label><T id="shopee.workspace.choose-variant" /></Label><Input type="select" value={variantId} disabled={!productId} onChange={event => setVariantId(event.target.value)}><option value="">—</option>{availableVariants.map(variant => <option key={variant.id} value={variant.id}>{variant.sku || `#${variant.id}`} · {money(variant.price)}</option>)}</Input></FormGroup> : null}
          <div className="text-muted small"><T id="shopee.workspace.mapping-help" /></div>
        </ModalBody>
        <ModalFooter>
          {mappingDialog && mappingDialog.product_id ? <Button color="outline-danger" disabled={workspace.busy[`mapping-${mappingDialog.id}`]} onClick={clearMapping}><T id="shopee.workspace.catalog.unmap" /></Button> : null}
          <Button color="secondary" onClick={() => setMappingDialog(null)}><T id="shopee.cancel" /></Button>
          <Button color="primary" disabled={!Number(productId) || (requiresVariant && !Number(variantId)) || workspace.busy[`mapping-${mappingDialog && mappingDialog.id}`]} onClick={saveMapping}><T id="shopee.workspace.save" /></Button>
        </ModalFooter>
      </Modal>
      {listingEditor ? <ShopeeListingEditor mode={listingEditor.mode} mapping={listingEditor.mapping} shops={workspace.shops} initialShopId={listingEditor.mapping && listingEditor.mapping.shop_id} isOpen onClose={() => setListingEditor(null)} runAction={workspace.runAction} busy={workspace.busy[listingEditor.mode === "update" ? `listing-update-${listingEditor.mapping && listingEditor.mapping.platform_item_id}` : "listing-publish"]} /> : null}
    </React.Fragment>
  );
  return (
    <Card className="mb-3">
      <CardBody>
        <div className="d-flex flex-wrap justify-content-between mb-3">
          <h5><T id={inventoryOnly ? "shopee.workspace.inventory" : "shopee.workspace.catalog"} /></h5>
          {writable ? (
            <div>
              {!inventoryOnly ? (
                <Button
                  color="success"
                  className="mr-2"
                  disabled={!hasConcreteShop}
                  onClick={() => openListingEditor({ mode: "publish" })}
                >
                  <T id="shopee.push.btn.push-short" />
                </Button>
              ) : null}
              <Button
                color="primary"
                disabled={
                  workspace.busy[
                    inventoryOnly ? "sync-inventory" : "import-catalog"
                  ]
                }
                onClick={action}
              >
                <T id={inventoryOnly ? "shopee.workspace.sync-stock" : "shopee.workspace.import-catalog"} />
              </Button>
            </div>
          ) : null}
        </div>
        <TableShell
          rowKey="id"
          rows={workspace.data.mappings}
          columns={[
            { key: "platform_name", label: <T id="shopee.products.col.name" /> },
            { key: "platform_sku", label: "SKU" },
            { key: "platform_model_id", label: "Model ID" },
            { key: "platform_stock", label: <T id="shopee.workspace.shopee-stock" /> },
            { key: "conflict_state", label: <T id="shopee.workspace.state" />, render: value => <Status value={value} /> }
          ]}
          actions={!inventoryOnly && writable ? row => {
            const firstItemRow =
              workspace.data.mappings.find(
                item =>
                  String(item.platform_item_id) ===
                  String(row.platform_item_id)
              ) === row;
            return (
            <div className="d-flex flex-wrap justify-content-end">
              <Button
                className="mr-1 mb-1"
                size="sm"
                color={row.product_id ? "outline-primary" : "warning"}
                disabled={workspace.busy[`mapping-${row.id}`]}
                onClick={() => openMapping(row)}
              >
                <T id="shopee.workspace.map-product" />
              </Button>
              {!row.product_id ? (
                <Button
                  className="mb-1"
                  size="sm"
                  color="outline-success"
                  disabled={workspace.busy[`draft-${row.platform_item_id}`]}
                  onClick={() =>
                    workspace.runAction(
                      `draft-${row.platform_item_id}`,
                      key =>
                        ShopeeV2Api.createCatalogDraft(
                          workspace.selectedShopId,
                          row.platform_item_id,
                          null,
                          key
                        ),
                      intl.formatMessage({
                        id: "shopee.workspace.catalog.draft-created"
                      })
                    )
                  }
                >
                  <T id="shopee.workspace.create-draft" />
                </Button>
              ) : null}
              {firstItemRow ? (
                <React.Fragment>
                  <Button
                    className="mr-1 mb-1"
                    size="sm"
                    color="outline-info"
                    disabled={
                      !hasConcreteShop ||
                      workspace.busy[
                        `listing-update-${row.platform_item_id}`
                      ]
                    }
                    onClick={() =>
                      openListingEditor({ mode: "update", mapping: row })
                    }
                  >
                    <T id="shopee.workspace.update-listing" />
                  </Button>
                  <Button
                    className="mr-1 mb-1"
                    size="sm"
                    color="outline-success"
                    disabled={
                      workspace.busy[`listing-show-${row.platform_item_id}`]
                    }
                    onClick={() =>
                      workspace.runAction(
                        `listing-show-${row.platform_item_id}`,
                        key =>
                          ShopeeV2Api.setListingStatus(
                            workspace.selectedShopId,
                            row.platform_item_id,
                            true,
                            key
                          ),
                        intl.formatMessage({
                          id: "shopee.workspace.catalog.listing-show-queued"
                        })
                      )
                    }
                  >
                    <T id="shopee.workspace.list-listing" />
                  </Button>
                  <Button
                    className="mb-1"
                    size="sm"
                    color="outline-danger"
                    disabled={
                      workspace.busy[`listing-hide-${row.platform_item_id}`]
                    }
                    onClick={() =>
                      workspace.runAction(
                        `listing-hide-${row.platform_item_id}`,
                        key =>
                          ShopeeV2Api.setListingStatus(
                            workspace.selectedShopId,
                            row.platform_item_id,
                            false,
                            key
                          ),
                        intl.formatMessage({
                          id: "shopee.workspace.catalog.listing-hide-queued"
                        })
                      )
                    }
                  >
                    <T id="shopee.workspace.unlist-listing" />
                  </Button>
                </React.Fragment>
              ) : null}
            </div>
          );} : null}
        />
      </CardBody>
      <Modal isOpen={Boolean(mappingDialog)} toggle={() => setMappingDialog(null)}>
        <ModalHeader toggle={() => setMappingDialog(null)}>
          <T id="shopee.workspace.mapping-wizard" />
        </ModalHeader>
        <ModalBody>
          {mappingDialog ? (
            <Alert color="info">
              {mappingDialog.platform_name || mappingDialog.platform_sku} — Model{" "}
              {mappingDialog.platform_model_id || "0"}
            </Alert>
          ) : null}
          <FormGroup>
            <Label><T id="shopee.workspace.search-product" /></Label>
            <Input
              value={candidateSearch}
              onChange={event => setCandidateSearch(event.target.value)}
              placeholder={intl.formatMessage({
                id: "shopee.workspace.catalog.sku-name-placeholder"
              })}
            />
          </FormGroup>
          <FormGroup>
            <Label><T id="shopee.workspace.choose-product" /></Label>
            <Input
              type="select"
              value={productId}
              disabled={candidatesLoading}
              onChange={event => {
                setProductId(event.target.value);
                setVariantId("");
              }}
            >
              <option value="">—</option>
              {candidates.map(candidate => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.name} ({candidate.sku})
                </option>
              ))}
            </Input>
          </FormGroup>
          {requiresVariant ? (
            <FormGroup>
              <Label><T id="shopee.workspace.choose-variant" /></Label>
              <Input
                type="select"
                value={variantId}
                disabled={!productId}
                onChange={event => setVariantId(event.target.value)}
              >
                <option value="">—</option>
                {availableVariants.map(variant => (
                  <option key={variant.id} value={variant.id}>
                    {variant.sku || `#${variant.id}`} · {money(variant.price)}
                  </option>
                ))}
              </Input>
            </FormGroup>
          ) : null}
          <div className="text-muted small">
            <T id="shopee.workspace.mapping-help" />
          </div>
        </ModalBody>
        <ModalFooter>
          {mappingDialog && mappingDialog.product_id ? (
            <Button
              color="outline-danger"
              disabled={workspace.busy[`mapping-${mappingDialog.id}`]}
              onClick={clearMapping}
            >
              <T id="shopee.workspace.catalog.unmap" />
            </Button>
          ) : null}
          <Button color="secondary" onClick={() => setMappingDialog(null)}>
            <T id="shopee.cancel" />
          </Button>
          <Button
            color="primary"
            disabled={
              !Number(productId) ||
              (requiresVariant && !Number(variantId)) ||
              workspace.busy[`mapping-${mappingDialog && mappingDialog.id}`]
            }
            onClick={saveMapping}
          >
            <T id="shopee.workspace.save" />
          </Button>
        </ModalFooter>
      </Modal>
      {!inventoryOnly && listingEditor ? (
        <ShopeeListingEditor
          mode={listingEditor.mode}
          mapping={listingEditor.mapping}
          shops={workspace.shops}
          initialShopId={
            listingEditor.mapping && listingEditor.mapping.shop_id
          }
          isOpen
          onClose={() => setListingEditor(null)}
          runAction={workspace.runAction}
          busy={
            workspace.busy[
              listingEditor.mode === "update"
                ? `listing-update-${
                    listingEditor.mapping &&
                    listingEditor.mapping.platform_item_id
                  }`
                : "listing-publish"
            ]
          }
        />
      ) : null}
    </Card>
  );
});

export const FulfillmentPanel = injectIntl(function FulfillmentPanel({
  intl
}) {
  const workspace = useContext(ShopeeWorkspaceContext);
  const writable = canWrite(workspace.capabilities, "shopee_fulfillment");
  const [selected, setSelected] = useState(null);
  const [parameters, setParameters] = useState(null);
  const [pii, setPii] = useState(null);
  const [mode, setMode] = useState("pickup");
  const [addressId, setAddressId] = useState("");
  const [timeId, setTimeId] = useState("");
  const [branchId, setBranchId] = useState("");

  const open = async row => {
    setSelected(row);
    setPii(null);
    try {
      const responses = await Promise.all([
        ShopeeV2Api.shippingParameters(workspace.selectedShopId, row.order_sn),
        ShopeeV2Api.orderPii(row.id)
      ]);
      setParameters(responses[0].data || {});
      setPii(responses[1].data || null);
    } catch (error) {
      setParameters({ error: true });
    }
  };
  const addresses = (parameters && parameters.addresses) || [];
  const slots =
    (parameters && parameters.pickup && parameters.pickup.time_slot_list) || [];
  const submit = () => {
    const payload = mode === "pickup"
      ? { pickup_type: "pickup", pickup: { address_id: Number(addressId), pickup_time_id: timeId } }
      : { pickup_type: "dropoff", dropoff: { branch_id: Number(branchId) } };
    workspace.runAction(
      `ship-${selected.order_sn}`,
      key => ShopeeV2Api.ship(workspace.selectedShopId, selected.order_sn, payload, key),
      intl.formatMessage({ id: "shopee.workspace.fulfillment.ship-queued" })
    );
    setSelected(null);
  };
  return (
    <Card className="mb-3">
      <CardBody>
        <h5><T id="shopee.workspace.fulfillment" /></h5>
        <TableShell
          rowKey="id"
          rows={workspace.data.orders}
          columns={[
            { key: "order_sn", label: <T id="shopee.orders.col.sn" /> },
            { key: "mapping_state", label: <T id="shopee.workspace.mapping" />, render: value => <Status value={value} /> },
            { key: "sync_state", label: <T id="shopee.workspace.reservation" />, render: value => <Status value={value} /> },
            { key: "tracking_number", label: <T id="shopee.workspace.tracking" /> }
          ]}
          actions={writable ? row => (
            <Button
              size="sm"
              color="primary"
              disabled={row.mapping_state !== "mapped" || row.sync_state !== "reserved"}
              onClick={() => open(row)}
            >
              <T id="shopee.workspace.ship" />
            </Button>
          ) : null}
        />
      </CardBody>
      <Modal isOpen={Boolean(selected)} toggle={() => setSelected(null)}>
        <ModalHeader toggle={() => setSelected(null)}><T id="shopee.workspace.ship-confirm" /></ModalHeader>
        <ModalBody>
          {parameters && parameters.error ? <Alert color="danger"><T id="shopee.workspace.shipping-params-error" /></Alert> : null}
          {pii ? (
            <Alert color="secondary">
              <strong>{pii.recipient_name}</strong>
              <div>{pii.recipient_phone}</div>
              <div>{pii.recipient_address}</div>
            </Alert>
          ) : null}
          <FormGroup>
            <Label><T id="shopee.workspace.shipping-method" /></Label>
            <Input type="select" value={mode} onChange={event => setMode(event.target.value)}>
              <option value="pickup">Pickup</option>
              <option value="dropoff">Dropoff</option>
            </Input>
          </FormGroup>
          {mode === "pickup" ? (
            <React.Fragment>
              <FormGroup>
                <Label><T id="shopee.workspace.pickup-address" /></Label>
                <Input type="select" value={addressId} onChange={event => setAddressId(event.target.value)}>
                  <option value="">—</option>
                  {addresses.map(address => <option key={address.address_id} value={address.address_id}>{address.address || address.address_id}</option>)}
                </Input>
              </FormGroup>
              <FormGroup>
                <Label><T id="shopee.workspace.pickup-time" /></Label>
                <Input type="select" value={timeId} onChange={event => setTimeId(event.target.value)}>
                  <option value="">—</option>
                  {slots.map(slot => <option key={slot.pickup_time_id} value={slot.pickup_time_id}>{slot.date || slot.pickup_time_id}</option>)}
                </Input>
              </FormGroup>
            </React.Fragment>
          ) : (
            <FormGroup>
              <Label>Branch ID</Label>
              <Input value={branchId} onChange={event => setBranchId(event.target.value)} />
            </FormGroup>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setSelected(null)}><T id="shopee.cancel" /></Button>
          <Button color="primary" disabled={parameters && parameters.error} onClick={submit}><T id="shopee.workspace.confirm" /></Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
});

const RETURN_STATUS_KEY_MAP = {
  REQUESTED: "shopee.workspace.returns.status.requested",
  IN_INSPECTION: "shopee.workspace.returns.status.in-inspection",
  WAITING_REFUND: "shopee.workspace.returns.status.waiting-refund",
  PROCESSING: "shopee.workspace.returns.status.processing",
  COMPLETED: "shopee.workspace.returns.status.completed",
  REJECTED: "shopee.workspace.returns.status.rejected",
  RESTOCKED: "shopee.workspace.returns.status.restocked",
  WAITING_CONFIRM: "shopee.workspace.returns.status.waiting-confirm"
};

const RETURN_STATUS_BADGE_STYLE = {
  IN_INSPECTION: { color: "info", pill: true, className: "px-2 py-1", style: {} },
  WAITING_REFUND: {
    color: "warning",
    pill: true,
    className: "px-2 py-1",
    style: { backgroundColor: "#fd7e14", color: "#fff" }
  },
  RESTOCKED: {
    color: "success",
    pill: true,
    className: "px-2 py-1",
    style: { backgroundColor: "#28a745" }
  },
  PROCESSING: { color: "primary", pill: true, className: "px-2 py-1", style: {} },
  COMPLETED: { color: "success", pill: true, className: "px-2 py-1", style: {} },
  REJECTED: { color: "danger", pill: true, className: "px-2 py-1", style: {} },
  WAITING_CONFIRM: { color: "warning", pill: true, className: "px-2 py-1", style: {} }
};

export const ReturnsPanel = injectIntl(function ReturnsPanel({ intl }) {
  const workspace = useContext(ShopeeWorkspaceContext);
  const writable = canWrite(workspace.capabilities, "shopee_return");

  // State for original API Action modal
  const [dialog, setDialog] = useState(null);
  const [reason, setReason] = useState("");
  const [evidence, setEvidence] = useState("");
  const [stockDecision, setStockDecision] = useState("RESELLABLE");

  // State for new Rich UI
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedChannel, setSelectedChannel] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedReason, setSelectedReason] = useState("all");
  const [selectedWarehouse, setSelectedWarehouse] = useState("all");
  const [selectedReturn, setSelectedReturn] = useState(null);

  // Drawer Action States
  const [inspectionChecklist, setInspectionChecklist] = useState({
    intact: true,
    packaging: true,
    accessories: true,
    correctProduct: true,
    hasDefect: false
  });
  const [drawerStockAction, setDrawerStockAction] = useState("RESTOCK");
  const [drawerWarehouse, setDrawerWarehouse] = useState("Kho HCM");
  const [drawerNote, setDrawerNote] = useState("");

  const openOriginalDialog = (row, action) => {
    setDialog({ row, action });
    setReason("");
    setEvidence("");
    setStockDecision("RESELLABLE");
  };

  const submitOriginalDialog = () => {
    if (!reason.trim()) return;
    const row = dialog.row;
    if (dialog.action === "confirm") {
      workspace.runAction(
        `return-${row.id}`,
        key => ShopeeV2Api.confirmReturn(workspace.selectedShopId, row.return_sn, `${reason} [StockAction: ${stockDecision}]`, key),
        intl.formatMessage({
          id: "shopee.workspace.returns.inspection-confirmed"
        })
      );
    } else {
      const images = evidence.split(",").map(value => value.trim()).filter(Boolean);
      if (!images.length) return;
      workspace.runAction(
        `dispute-${row.id}`,
        key => ShopeeV2Api.disputeReturn(
          workspace.selectedShopId,
          row.return_sn,
          { dispute_reason: reason, images },
          key
        ),
        intl.formatMessage({
          id: "shopee.workspace.returns.dispute-queued"
        })
      );
    }
    setDialog(null);
  };

  // Use real API data only
  const returnList = useMemo(() => {
    const apiReturns = workspace.data && workspace.data.returns ? workspace.data.returns : [];
    return apiReturns.map(item => ({
      id: String(item.id || item.return_sn),
      return_sn: item.return_sn || `RT-${item.id}`,
      order_sn: item.order_sn || item.ordersn || "",
      customer_name: item.user_name || item.buyer_username || intl.formatMessage({ id: "shopee.workspace.returns.customer-fallback" }),
      customer_phone: item.phone || "",
      customer_address: item.address || "",
      is_vip: false,
      channel: "shopee",
      reason: item.reason || intl.formatMessage({ id: "shopee.workspace.returns.reason-other" }),
      reason_detail: item.text_reason || item.reason || intl.formatMessage({ id: "shopee.workspace.returns.reason-detail-fallback" }),
      refund_amount: Number(item.refund_amount || item.amount || 0),
      warehouse: "Kho HCM",
      status: item.status || "PROCESSING",
      status_code: item.status || "PROCESSING",
      created_at: item.create_time ? new Date(item.create_time * 1000).toLocaleString("vi-VN") : item.created_at || "",
      items: (item.item_list || item.items || []).map(p => ({
        name: p.name || p.item_name || intl.formatMessage({ id: "shopee.workspace.returns.item-fallback" }),
        sku: p.model_sku || p.item_sku || p.sku || "",
        qty: Number(p.amount || p.quantity || p.qty || 1),
        price: Number(p.item_price || p.price || 0),
        img: p.image_url || p.img || ""
      })),
      customer_images: item.image_list || item.images || []
    }));
  }, [workspace.data.returns]);

  // Filter returns based on search and dropdown selections
  const filteredReturns = useMemo(() => {
    return returnList.filter(item => {
      // Tab filter
      if (activeTab !== "all" && item.channel !== activeTab) {
        return false;
      }
      // Channel dropdown filter
      if (selectedChannel !== "all" && item.channel !== selectedChannel) {
        return false;
      }
      // Status dropdown filter
      if (selectedStatus !== "all" && item.status_code !== selectedStatus) {
        return false;
      }
      // Reason filter
      if (selectedReason !== "all" && item.reason !== selectedReason) {
        return false;
      }
      // Warehouse filter
      if (selectedWarehouse !== "all" && item.warehouse !== selectedWarehouse) {
        return false;
      }
      // Search query filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchSn = item.return_sn.toLowerCase().includes(q);
        const matchOrder = item.order_sn.toLowerCase().includes(q);
        const matchName = item.customer_name.toLowerCase().includes(q);
        const matchPhone = item.customer_phone.includes(q);
        if (!matchSn && !matchOrder && !matchName && !matchPhone) return false;
      }
      return true;
    });
  }, [returnList, activeTab, selectedChannel, selectedStatus, selectedReason, selectedWarehouse, searchQuery]);

  // Helper renderers
  const renderChannelBadge = (channel) => {
    switch (channel) {
      case "shopee":
        return <span className="badge text-white px-2 py-1" style={{ backgroundColor: "#EE4D2D", borderRadius: 4 }}>S</span>;
      case "tiktok":
        return <span className="badge text-white px-2 py-1" style={{ backgroundColor: "#000000", borderRadius: 4 }}>🎵</span>;
      case "lazada":
        return <span className="badge text-white px-2 py-1" style={{ backgroundColor: "#0F146D", borderRadius: 4 }}>L</span>;
      case "website":
        return <span className="badge text-white px-2 py-1" style={{ backgroundColor: "#0088FE", borderRadius: 4 }}>🌐</span>;
      default:
        return <span className="badge text-white px-2 py-1" style={{ backgroundColor: "#6c757d", borderRadius: 4 }}>🛒</span>;
    }
  };

  const renderStatusBadge = (statusCode) => {
    const labelId = RETURN_STATUS_KEY_MAP[statusCode];
    const style = RETURN_STATUS_BADGE_STYLE[statusCode] || {
      color: "secondary",
      pill: true,
      className: "px-2 py-1",
      style: {}
    };
    return (
      <Badge color={style.color} pill={style.pill} className={style.className} style={style.style}>
        {labelId ? <T id={labelId} /> : statusCode}
      </Badge>
    );
  };

  // ReactTable Columns Configuration
  const tableColumns = useMemo(() => [
    {
      Header: "",
      accessor: "id",
      width: 40,
      Cell: () => <input type="checkbox" className="custom-control-input-style" />
    },
    {
      Header: <T id="shopee.workspace.returns.col.sn" />,
      accessor: "return_sn",
      Cell: ({ row, value }) => (
        <span
          className="font-weight-bold text-primary cursor-pointer"
          onClick={() => setSelectedReturn(row._original)}
          style={{ textDecoration: "underline" }}
        >
          {value}
        </span>
      )
    },
    {
      Header: <T id="shopee.workspace.returns.col.order" />,
      accessor: "order_sn",
      Cell: ({ value }) => <span className="text-muted small">{value}</span>
    },
    {
      Header: <T id="shopee.workspace.common.customer" />,
      accessor: "customer_name",
      Cell: ({ row }) => (
        <div>
          <div className="font-weight-bold text-dark">{row._original.customer_name}</div>
          <div className="small text-muted">{row._original.customer_phone}</div>
        </div>
      )
    },
    {
      Header: <T id="shopee.workspace.returns.col.channel" />,
      accessor: "channel",
      width: 60,
      Cell: ({ value }) => <div className="text-center">{renderChannelBadge(value)}</div>
    },
    {
      Header: <T id="shopee.workspace.returns.col.reason" />,
      accessor: "reason",
      Cell: ({ value }) => <span>{value}</span>
    },
    {
      Header: <T id="shopee.workspace.returns.col.refund-value" />,
      accessor: "refund_amount",
      Cell: ({ value }) => <span className="font-weight-bold text-dark">{money(value)} đ</span>
    },
    {
      Header: <T id="shopee.workspace.returns.col.warehouse" />,
      accessor: "warehouse",
      Cell: ({ value }) => <span className="text-muted">{value}</span>
    },
    {
      Header: <T id="shopee.workspace.returns.col.status" />,
      accessor: "status_code",
      Cell: ({ value }) => renderStatusBadge(value)
    },
    {
      Header: <T id="shopee.workspace.returns.col.created-at" />,
      accessor: "created_at",
      Cell: ({ value }) => <span className="small text-muted">{value}</span>
    },
    {
      Header: <T id="shopee.workspace.actions" />,
      accessor: "actions",
      width: 100,
      Cell: ({ row }) => (
        <div className="d-flex align-items-center">
          <Button
            outline
            color="secondary"
            size="xs"
            className="btn-icon rounded-circle mr-1"
            title={intl.formatMessage({ id: "shopee.workspace.returns.view-detail-title" })}
            onClick={() => setSelectedReturn(row._original)}
          >
            👁
          </Button>
          <UncontrolledDropdown>
            <DropdownToggle color="light" size="xs" className="btn-icon rounded-circle">
              •••
            </DropdownToggle>
            <DropdownMenu right>
              <DropdownItem onClick={() => openOriginalDialog(row._original, "confirm")}>
                <T id="shopee.workspace.returns.action.inspect" />
              </DropdownItem>
              <DropdownItem onClick={() => openOriginalDialog(row._original, "dispute")}>
                <T id="shopee.workspace.returns.action.dispute" />
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>
      )
    }
  ], []);

  // Compute KPIs from real data
  const kpiStats = useMemo(() => {
    const byStatus = {};
    let totalRefund = 0;
    returnList.forEach(item => {
      const sc = item.status_code || item.status || "";
      byStatus[sc] = (byStatus[sc] || 0) + 1;
      totalRefund += item.refund_amount || 0;
    });
    const requested = (byStatus.REQUESTED || 0) + (byStatus.WAITING_CONFIRM || 0);
    const processing = (byStatus.IN_INSPECTION || 0) + (byStatus.PROCESSING || 0);
    const waitingRefund = byStatus.WAITING_REFUND || 0;
    const completed = (byStatus.COMPLETED || 0) + (byStatus.RESTOCKED || 0);
    const rejected = byStatus.REJECTED || 0;
    // Reason breakdown
    const reasons = {};
    returnList.forEach(item => {
      const r = item.reason || "Khác";
      reasons[r] = (reasons[r] || 0) + 1;
    });
    const sortedReasons = Object.entries(reasons)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([name, count]) => ({ name, count, pct: returnList.length ? Math.round((count / returnList.length) * 100) : 0 }));
    // Top returned products
    const products = {};
    returnList.forEach(item => {
      (item.items || []).forEach(p => {
        const key = p.sku || p.name || "unknown";
        if (!products[key]) products[key] = { name: p.name, sku: p.sku, count: 0 };
        products[key].count += p.qty || 1;
      });
    });
    const topProducts = Object.values(products).sort((a, b) => b.count - a.count).slice(0, 5);
    return { requested, processing, waitingRefund, completed, rejected, totalRefund, total: returnList.length, sortedReasons, topProducts };
  }, [returnList]);

  return (
    <div className="shopee-returns-panel w-100" style={{ background: "#f8f9fa", minHeight: "100vh" }}>
      {/* 1. Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-4 pt-2">
        <div>
          <h2 className="font-weight-bold mb-1" style={{ color: "#1e293b", fontSize: 24 }}>
            <T id="shopee.workspace.tab.returns" />
          </h2>
          <p className="text-muted mb-0" style={{ fontSize: 13 }}>
            <T id="shopee.workspace.returns.subtitle" />
          </p>
        </div>
        <div className="d-flex align-items-center">
          <Button color="outline-secondary" className="mr-2 px-3 shadow-sm bg-white" style={{ borderRadius: 6, fontWeight: 500 }}>
            <T id="shopee.workspace.returns.export-excel" />
          </Button>
          <Button color="outline-secondary" className="mr-2 px-3 shadow-sm bg-white" style={{ borderRadius: 6, fontWeight: 500 }}>
            <T id="shopee.workspace.returns.report" />
          </Button>
          <Button color="primary" className="px-3 shadow-sm" style={{ borderRadius: 6, backgroundColor: "#0066FE", borderColor: "#0066FE", fontWeight: 600 }}>
            + <T id="shopee.workspace.returns.create-request" />
          </Button>
        </div>
      </div>

      {/* 2. Top KPI Cards Row */}
      <Row className="mb-4">
        <Col xs="12" sm="6" md="4" lg="2" className="mb-2 mb-lg-0">
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 10 }}>
            <CardBody className="p-3">
              <div className="d-flex align-items-center mb-2">
                <div className="rounded-circle p-2 mr-2" style={{ backgroundColor: "#fff7ed", color: "#ea580c" }}>
                  📦
                </div>
                <span className="text-muted font-weight-bold" style={{ fontSize: 12 }}><T id="shopee.workspace.returns.status.requested" /></span>
              </div>
              <h3 className="font-weight-bold mb-1" style={{ color: "#0f172a" }}>{kpiStats.requested}</h3>
              <div className="small text-muted font-weight-normal">
                <T id="shopee.workspace.returns.kpi-requested-note" />
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col xs="12" sm="6" md="4" lg="2" className="mb-2 mb-lg-0">
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 10 }}>
            <CardBody className="p-3">
              <div className="d-flex align-items-center mb-2">
                <div className="rounded-circle p-2 mr-2" style={{ backgroundColor: "#eff6ff", color: "#2563eb" }}>
                  ⏳
                </div>
                <span className="text-muted font-weight-bold" style={{ fontSize: 12 }}><T id="shopee.workspace.returns.status.processing" /></span>
              </div>
              <h3 className="font-weight-bold mb-1" style={{ color: "#0f172a" }}>{kpiStats.processing}</h3>
              <div className="small text-muted font-weight-normal">
                <T id="shopee.workspace.returns.kpi-processing-note" />
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col xs="12" sm="6" md="4" lg="2" className="mb-2 mb-lg-0">
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 10 }}>
            <CardBody className="p-3">
              <div className="d-flex align-items-center mb-2">
                <div className="rounded-circle p-2 mr-2" style={{ backgroundColor: "#fff7ed", color: "#d97706" }}>
                  🛍️
                </div>
                <span className="text-muted font-weight-bold" style={{ fontSize: 12 }}><T id="shopee.workspace.returns.status.waiting-refund" /></span>
              </div>
              <h3 className="font-weight-bold mb-1" style={{ color: "#0f172a" }}>{kpiStats.waitingRefund}</h3>
              <div className="small text-muted font-weight-normal">
                <T id="shopee.workspace.returns.kpi-waiting-refund-note" />
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col xs="12" sm="6" md="4" lg="2" className="mb-2 mb-lg-0">
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 10 }}>
            <CardBody className="p-3">
              <div className="d-flex align-items-center mb-2">
                <div className="rounded-circle p-2 mr-2" style={{ backgroundColor: "#f0fdf4", color: "#16a34a" }}>
                  ✅
                </div>
                <span className="text-muted font-weight-bold" style={{ fontSize: 12 }}><T id="shopee.workspace.returns.status.completed" /></span>
              </div>
              <h3 className="font-weight-bold mb-1" style={{ color: "#0f172a" }}>{kpiStats.completed}</h3>
              <div className="small text-muted font-weight-normal">
                <T id="shopee.workspace.returns.kpi-completed-note" />
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col xs="12" sm="6" md="4" lg="2" className="mb-2 mb-lg-0">
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 10 }}>
            <CardBody className="p-3">
              <div className="d-flex align-items-center mb-2">
                <div className="rounded-circle p-2 mr-2" style={{ backgroundColor: "#fef2f2", color: "#dc2626" }}>
                  🚫
                </div>
                <span className="text-muted font-weight-bold" style={{ fontSize: 12 }}><T id="shopee.workspace.returns.status.rejected" /></span>
              </div>
              <h3 className="font-weight-bold mb-1" style={{ color: "#0f172a" }}>{kpiStats.rejected}</h3>
              <div className="small text-muted font-weight-normal">
                <T id="shopee.workspace.returns.kpi-rejected-note" />
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col xs="12" sm="6" md="4" lg="2" className="mb-2 mb-lg-0">
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 10, backgroundColor: "#ffffff" }}>
            <CardBody className="p-3 d-flex flex-column justify-content-center">
              <div className="mb-2">
                <div className="text-muted font-weight-bold" style={{ fontSize: 11 }}><T id="shopee.workspace.returns.total-refund-today" /></div>
                <div className="font-weight-bold text-dark" style={{ fontSize: 16 }}>{money(kpiStats.totalRefund)}</div>
              </div>
              <div>
                <div className="text-muted font-weight-bold" style={{ fontSize: 11 }}><T id="shopee.workspace.returns.return-value-today" /></div>
                <div className="font-weight-bold text-primary" style={{ fontSize: 16 }}>{money(kpiStats.totalRefund)}</div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* 3. Process Workflow Pipeline */}
      <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: 10 }}>
        <CardBody className="py-3 px-4">
          <div className="d-flex justify-content-between align-items-center text-center overflow-auto">
            <div className="px-2">
              <div className="rounded-circle mx-auto mb-1 d-flex align-items-center justify-content-center" style={{ width: 36, height: 36, backgroundColor: "#ede9fe", color: "#7c3aed" }}>
                📥
              </div>
              <div className="small text-muted font-weight-bold"><T id="shopee.workspace.returns.pipeline.customer-request" /></div>
              <div className="font-weight-bold text-primary">{kpiStats.requested}</div>
            </div>

            <span className="text-muted mx-1">➔</span>

            <div className="px-2">
              <div className="rounded-circle mx-auto mb-1 d-flex align-items-center justify-content-center" style={{ width: 36, height: 36, backgroundColor: "#fff7ed", color: "#ea580c" }}>
                📋
              </div>
              <div className="small text-muted font-weight-bold"><T id="shopee.workspace.returns.status.waiting-confirm" /></div>
              <div className="font-weight-bold text-primary">{kpiStats.requested}</div>
            </div>

            <span className="text-muted mx-1">➔</span>

            <div className="px-2">
              <div className="rounded-circle mx-auto mb-1 d-flex align-items-center justify-content-center" style={{ width: 36, height: 36, backgroundColor: "#eff6ff", color: "#2563eb" }}>
                🔍
              </div>
              <div className="small text-muted font-weight-bold"><T id="shopee.workspace.returns.pipeline.inspect" /></div>
              <div className="font-weight-bold text-primary">{kpiStats.processing}</div>
            </div>

            <span className="text-muted mx-1">➔</span>

            <div className="px-2">
              <div className="rounded-circle mx-auto mb-1 d-flex align-items-center justify-content-center" style={{ width: 36, height: 36, backgroundColor: "#fef3c7", color: "#d97706" }}>
                ✍️
              </div>
              <div className="small text-muted font-weight-bold"><T id="shopee.workspace.returns.pipeline.approve" /></div>
              <div className="font-weight-bold text-primary">{kpiStats.processing}</div>
            </div>

            <span className="text-muted mx-1">➔</span>

            <div className="px-2">
              <div className="rounded-circle mx-auto mb-1 d-flex align-items-center justify-content-center" style={{ width: 36, height: 36, backgroundColor: "#f0fdf4", color: "#16a34a" }}>
                🏬
              </div>
              <div className="small text-muted font-weight-bold"><T id="shopee.workspace.returns.pipeline.restock-dispose" /></div>
              <div className="font-weight-bold text-primary">{kpiStats.completed}</div>
            </div>

            <span className="text-muted mx-1">➔</span>

            <div className="px-2">
              <div className="rounded-circle mx-auto mb-1 d-flex align-items-center justify-content-center" style={{ width: 36, height: 36, backgroundColor: "#fef2f2", color: "#dc2626" }}>
                💵
              </div>
              <div className="small text-muted font-weight-bold"><T id="shopee.workspace.returns.pipeline.refund" /></div>
              <div className="font-weight-bold text-primary">{kpiStats.waitingRefund}</div>
            </div>

            <span className="text-muted mx-1">➔</span>

            <div className="px-2">
              <div className="rounded-circle mx-auto mb-1 d-flex align-items-center justify-content-center" style={{ width: 36, height: 36, backgroundColor: "#dcfce7", color: "#15803d" }}>
                🎉
              </div>
              <div className="small text-muted font-weight-bold"><T id="shopee.workspace.returns.status.completed" /></div>
              <div className="font-weight-bold text-primary">{kpiStats.completed}</div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* 4. Advanced Filter Toolbar */}
      <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: 10 }}>
        <CardBody className="p-3">
          <Row className="align-items-center">
              <Col xs="12" md="4" className="mb-2 mb-md-0">
              <div className="position-relative">
                <Input
                  type="text"
                  placeholder={intl.formatMessage({ id: "shopee.workspace.returns.search-placeholder" })}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  style={{ borderRadius: 6, paddingLeft: 36, backgroundColor: "#f8fafc" }}
                />
                <span className="position-absolute text-muted" style={{ left: 12, top: 8 }}>🔍</span>
              </div>
            </Col>

            <Col xs="6" sm="3" md="2" className="mb-2 mb-md-0">
              <Input
                type="select"
                value={selectedChannel}
                onChange={e => setSelectedChannel(e.target.value)}
                style={{ borderRadius: 6, backgroundColor: "#f8fafc" }}
              >
                <option value="all"><T id="shopee.workspace.common.all-channels" /></option>
                <option value="shopee">Shopee</option>
                <option value="tiktok">TikTok Shop</option>
                <option value="lazada">Lazada</option>
                <option value="website">Website</option>
                <option value="pos">POS</option>
              </Input>
            </Col>

            <Col xs="6" sm="3" md="2" className="mb-2 mb-md-0">
              <Input
                type="select"
                value={selectedStatus}
                onChange={e => setSelectedStatus(e.target.value)}
                style={{ borderRadius: 6, backgroundColor: "#f8fafc" }}
              >
                <option value="all"><T id="shopee.workspace.common.all-statuses" /></option>
                {["IN_INSPECTION", "WAITING_REFUND", "RESTOCKED", "PROCESSING", "COMPLETED", "REJECTED"].map(code => (
                  <option key={code} value={code}>
                    <T id={RETURN_STATUS_KEY_MAP[code]} />
                  </option>
                ))}
              </Input>
            </Col>

            <Col xs="6" sm="3" md="1.5" className="mb-2 mb-md-0">
              <Input
                type="select"
                value={selectedReason}
                onChange={e => setSelectedReason(e.target.value)}
                style={{ borderRadius: 6, backgroundColor: "#f8fafc" }}
              >
                <option value="all"><T id="shopee.workspace.returns.all-reasons" /></option>
                <option value="Sai màu sắc">Sai màu sắc</option>
                <option value="Không vừa size">Không vừa size</option>
                <option value="Lỗi sản phẩm">Lỗi sản phẩm</option>
                <option value="Thiếu hàng">Thiếu hàng</option>
                <option value="Không thích">Không thích</option>
              </Input>
            </Col>

            <Col xs="6" sm="3" md="1.5" className="mb-2 mb-md-0">
              <Input
                type="select"
                value={selectedWarehouse}
                onChange={e => setSelectedWarehouse(e.target.value)}
                style={{ borderRadius: 6, backgroundColor: "#f8fafc" }}
              >
                <option value="all"><T id="shopee.workspace.returns.all-warehouses" /></option>
                <option value="Kho HCM">Kho HCM</option>
                <option value="Kho Hà Nội">Kho Hà Nội</option>
              </Input>
            </Col>

            <Col xs="12" md="1" className="d-flex justify-content-end">
              <Button outline color="secondary" className="w-100" style={{ borderRadius: 6 }}>
                <T id="shopee.workspace.common.filter" />
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* 5. Channel Nav Tabs */}
      <Nav tabs className="border-0 mb-3" style={{ borderBottom: "1px solid #e2e8f0" }}>
        <NavItem>
          <NavLink
            className={`font-weight-bold cursor-pointer border-0 ${activeTab === "all" ? "active text-primary" : "text-muted"}`}
            style={{ borderBottom: activeTab === "all" ? "2px solid #0066FE" : "none", backgroundColor: "transparent" }}
            onClick={() => setActiveTab("all")}
          >
            <IntlMessages
              id="shopee.workspace.common.tab-all-count"
              values={{ count: returnList.length }}
            />
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={`font-weight-bold cursor-pointer border-0 ${activeTab === "shopee" ? "active text-primary" : "text-muted"}`}
            style={{ borderBottom: activeTab === "shopee" ? "2px solid #0066FE" : "none", backgroundColor: "transparent" }}
            onClick={() => setActiveTab("shopee")}
          >
            Shopee ({returnList.filter(r => r.channel === "shopee").length})
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={`font-weight-bold cursor-pointer border-0 ${activeTab === "tiktok" ? "active text-primary" : "text-muted"}`}
            style={{ borderBottom: activeTab === "tiktok" ? "2px solid #0066FE" : "none", backgroundColor: "transparent" }}
            onClick={() => setActiveTab("tiktok")}
          >
            TikTok Shop ({returnList.filter(r => r.channel === "tiktok").length})
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={`font-weight-bold cursor-pointer border-0 ${activeTab === "lazada" ? "active text-primary" : "text-muted"}`}
            style={{ borderBottom: activeTab === "lazada" ? "2px solid #0066FE" : "none", backgroundColor: "transparent" }}
            onClick={() => setActiveTab("lazada")}
          >
            Lazada ({returnList.filter(r => r.channel === "lazada").length})
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={`font-weight-bold cursor-pointer border-0 ${activeTab === "website" ? "active text-primary" : "text-muted"}`}
            style={{ borderBottom: activeTab === "website" ? "2px solid #0066FE" : "none", backgroundColor: "transparent" }}
            onClick={() => setActiveTab("website")}
          >
            Website ({returnList.filter(r => r.channel === "website").length})
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={`font-weight-bold cursor-pointer border-0 ${activeTab === "pos" ? "active text-primary" : "text-muted"}`}
            style={{ borderBottom: activeTab === "pos" ? "2px solid #0066FE" : "none", backgroundColor: "transparent" }}
            onClick={() => setActiveTab("pos")}
          >
            POS ({returnList.filter(r => r.channel === "pos").length})
          </NavLink>
        </NavItem>
      </Nav>

      {/* 6. Main Data Table & Right Side Detail Drawer */}
      <Row className="mb-4">
        {/* Left Side: Table */}
        <Col xs="12" lg={selectedReturn ? "8" : "12"}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: 10 }}>
            <CardBody className="p-0">
              <ReactTable
                data={filteredReturns}
                columns={tableColumns}
                defaultPageSize={8}
                showPageSizeOptions={true}
                className="-striped -highlight border-0"
                previousText={intl.formatMessage({ id: "shopee.workspace.common.previous" })}
                nextText={intl.formatMessage({ id: "shopee.workspace.common.next" })}
                loadingText={intl.formatMessage({ id: "shopee.workspace.common.loading" })}
                noDataText={intl.formatMessage({ id: "shopee.workspace.returns.no-data" })}
                pageText={intl.formatMessage({ id: "shopee.workspace.common.page" })}
                ofText={intl.formatMessage({ id: "shopee.workspace.common.of" })}
                rowsText={intl.formatMessage({ id: "shopee.workspace.common.rows" })}
              />
            </CardBody>
          </Card>
        </Col>

        {/* Right Side: Detail Drawer Panel */}
        {selectedReturn && (
          <Col xs="12" lg="4" className="mt-3 mt-lg-0">
            <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 10, backgroundColor: "#ffffff" }}>
              <CardBody className="p-3">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                  <div className="d-flex align-items-center">
                    <span className="text-muted mr-2 cursor-pointer font-weight-bold" onClick={() => setSelectedReturn(null)}>
                      ‹
                    </span>
                    <h6 className="font-weight-bold mb-0 text-dark" style={{ fontSize: 15 }}>
                      <T id="shopee.workspace.returns.detail-title" />
                    </h6>
                  </div>
                  <Button close onClick={() => setSelectedReturn(null)} />
                </div>

                {/* Return ID & Status */}
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="d-flex align-items-center">
                    <h5 className="font-weight-bold text-primary mb-0 mr-2">{selectedReturn.return_sn}</h5>
                    {renderStatusBadge(selectedReturn.status)}
                  </div>
                  <div>{renderChannelBadge(selectedReturn.channel)}</div>
                </div>
                <div className="small text-muted mb-3"><T id="shopee.workspace.returns.original-order" />: <strong>{selectedReturn.order_sn}</strong></div>

                {/* Customer Info Box */}
                <div className="p-2 mb-3 rounded" style={{ backgroundColor: "#f8fafc", border: "1px solid #f1f5f9" }}>
                  <div className="d-flex justify-content-between align-items-start mb-1">
                    <div>
                      <span className="font-weight-bold text-dark">{selectedReturn.customer_name}</span>
                      {selectedReturn.is_vip && <Badge color="warning" className="ml-1 px-1">VIP</Badge>}
                    </div>
                    <div>
                      <Button size="xs" color="outline-primary" className="btn-icon rounded-circle mr-1">📞</Button>
                      <Button size="xs" color="outline-primary" className="btn-icon rounded-circle">💬</Button>
                    </div>
                  </div>
                  <div className="small text-muted mb-1">📞 {selectedReturn.customer_phone}</div>
                  <div className="small text-muted">📍 {selectedReturn.customer_address}</div>
                </div>

                {/* Return Reason */}
                <div className="mb-3">
                  <div className="small text-muted font-weight-bold"><T id="shopee.workspace.returns.col.reason" /></div>
                  <div className="font-weight-bold text-dark">{selectedReturn.reason}</div>
                  <div className="small text-muted">{selectedReturn.reason_detail}</div>
                </div>

                {/* Photos / Videos */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="small text-muted font-weight-bold"><T id="shopee.workspace.returns.customer-media" /></span>
                    <span className="small text-primary cursor-pointer"><T id="shopee.workspace.common.view-all" /></span>
                  </div>
                  <div className="d-flex">
                    {selectedReturn.customer_images && selectedReturn.customer_images.length > 0 ? (
                      selectedReturn.customer_images.map((img, idx) => (
                        <div key={idx} className="mr-2 position-relative" style={{ width: 60, height: 60, borderRadius: 6, overflow: "hidden" }}>
                          <img src={img} alt="evidence" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          {idx === 1 && <span className="position-absolute text-white font-weight-bold" style={{ inset: 0, backgroundColor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center" }}>▶</span>}
                          {idx === 2 && <span className="position-absolute text-white font-weight-bold" style={{ inset: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>+2</span>}
                        </div>
                      ))
                    ) : (
                      <div className="text-muted small italic"><T id="shopee.workspace.returns.no-media" /></div>
                    )}
                  </div>
                </div>

                {/* Returned Products */}
                <div className="mb-3">
                  <div className="small text-muted font-weight-bold mb-2"><T id="shopee.workspace.returns.returned-products" /></div>
                  {selectedReturn.items && selectedReturn.items.map((prod, idx) => (
                    <div key={idx} className="d-flex justify-content-between align-items-center mb-2 pb-1 border-bottom">
                      <div className="d-flex align-items-center">
                        <img src={prod.img} alt={prod.name} style={{ width: 36, height: 36, borderRadius: 4, objectFit: "cover" }} className="mr-2" />
                        <div>
                          <div className="small font-weight-bold text-dark">{prod.name}</div>
                          <div className="small text-muted">{prod.sku} | <T id="shopee.workspace.returns.qty-label" />: {prod.qty}</div>
                        </div>
                      </div>
                      <div className="small font-weight-bold text-dark">{money(prod.price)} đ</div>
                    </div>
                  ))}
                </div>

                {/* Inspection Checklist */}
                <div className="mb-3 p-2 rounded" style={{ backgroundColor: "#f8fafc", border: "1px solid #f1f5f9" }}>
                  <div className="small font-weight-bold text-dark mb-2"><T id="shopee.workspace.returns.inspection-title" /></div>
                  <div className="d-flex justify-content-between align-items-center small mb-1">
                    <span><T id="shopee.workspace.returns.check.intact" /></span>
                    <span className="text-success">✅</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center small mb-1">
                    <span><T id="shopee.workspace.returns.check.packaging" /></span>
                    <span className="text-success">✅</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center small mb-1">
                    <span><T id="shopee.workspace.returns.check.accessories" /></span>
                    <span className="text-success">✅</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center small mb-1">
                    <span><T id="shopee.workspace.returns.check.correct-product" /></span>
                    <span className="text-success">✅</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center small">
                    <span><T id="shopee.workspace.returns.check.defect" /></span>
                    <span className="text-muted">📷 📹</span>
                  </div>
                </div>

                {/* Return Action Buttons */}
                <div className="mb-3">
                  <div className="small font-weight-bold text-dark mb-2"><T id="shopee.workspace.returns.processing-title" /></div>
                  <div className="d-flex flex-wrap gap-1 mb-2">
                    <Button
                      size="xs"
                      color={drawerStockAction === "RESTOCK" ? "success" : "outline-success"}
                      className="mr-1 mb-1"
                      onClick={() => setDrawerStockAction("RESTOCK")}
                    >
                      <T id="shopee.workspace.returns.action.restock" />
                    </Button>
                    <Button
                      size="xs"
                      color={drawerStockAction === "EXCHANGE" ? "primary" : "outline-primary"}
                      className="mr-1 mb-1"
                      onClick={() => setDrawerStockAction("EXCHANGE")}
                    >
                      <T id="shopee.workspace.returns.action.exchange" />
                    </Button>
                    <Button
                      size="xs"
                      color={drawerStockAction === "REFUND" ? "warning" : "outline-warning"}
                      className="mr-1 mb-1"
                      onClick={() => setDrawerStockAction("REFUND")}
                    >
                      <T id="shopee.workspace.returns.pipeline.refund" />
                    </Button>
                    <Button
                      size="xs"
                      color={drawerStockAction === "REJECT" ? "danger" : "outline-danger"}
                      className="mr-1 mb-1"
                      onClick={() => setDrawerStockAction("REJECT")}
                    >
                      <T id="shopee.workspace.returns.status.rejected" />
                    </Button>
                    <Button
                      size="xs"
                      color={drawerStockAction === "DISCARD" ? "secondary" : "outline-secondary"}
                      className="mb-1"
                      onClick={() => setDrawerStockAction("DISCARD")}
                    >
                      <T id="shopee.workspace.returns.action.discard" />
                    </Button>
                  </div>

                  <FormGroup className="mb-2">
                    <Label className="small mb-1"><T id="shopee.workspace.returns.restock-warehouse" /></Label>
                    <Input
                      type="select"
                      bsSize="sm"
                      value={drawerWarehouse}
                      onChange={e => setDrawerWarehouse(e.target.value)}
                    >
                      <option value="Kho HCM">Kho HCM</option>
                      <option value="Kho Hà Nội">Kho Hà Nội</option>
                    </Input>
                  </FormGroup>

                  <FormGroup className="mb-2">
                    <Label className="small mb-1">
                      <T id="shopee.workspace.common.note" />{" "}
                      <span className="text-muted">(<T id="shopee.workspace.common.optional" />)</span>
                    </Label>
                    <Input
                      type="textarea"
                      bsSize="sm"
                      rows={2}
                      placeholder={intl.formatMessage({ id: "shopee.workspace.common.note-placeholder" })}
                      value={drawerNote}
                      onChange={e => setDrawerNote(e.target.value)}
                    />
                  </FormGroup>
                </div>

                {/* Footer Buttons */}
                <div className="d-flex justify-content-between pt-2 border-top">
                  <Button outline color="secondary" size="sm" className="px-3" onClick={() => setSelectedReturn(null)}>
                    <T id="shopee.workspace.save" />
                  </Button>
                  <Button
                    color="primary"
                    size="sm"
                    className="px-3 font-weight-bold"
                    style={{ backgroundColor: "#0066FE" }}
                    onClick={() => {
                      if (writable && selectedReturn) {
                        openOriginalDialog(selectedReturn, "confirm");
                      }
                    }}
                  >
                    <T id="shopee.workspace.returns.confirm-continue" />
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        )}
      </Row>

      {/* 7. Bottom Analytics Cards (4 Widgets Grid) */}
      <Row className="mb-4">
        {/* Widget 1: Popular Reasons Donut */}
        <Col xs="12" md="6" lg="3" className="mb-3 mb-lg-0">
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 10 }}>
            <CardBody className="p-3">
              <h6 className="font-weight-bold text-dark mb-3" style={{ fontSize: 14 }}>
                <T id="shopee.workspace.returns.popular-reasons" />
              </h6>
              <div className="d-flex align-items-center justify-content-around my-2">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center text-center font-weight-bold"
                  style={{
                    width: 110,
                    height: 110,
                    background: "conic-gradient(#0066FE 0% 38%, #38bdf8 38% 59%, #f59e0b 59% 77%, #ef4444 77% 89%, #8b5cf6 89% 96%, #94a3b8 96% 100%)",
                    position: "relative"
                  }}
                >
                  <div
                    className="rounded-circle bg-white d-flex flex-column align-items-center justify-content-center"
                    style={{ width: 75, height: 75 }}
                  >
                    <span className="small text-muted"><T id="shopee.workspace.common.total" /></span>
                    <span className="font-weight-bold text-dark">{kpiStats.total}</span>
                  </div>
                </div>

                <div className="small">
                  {kpiStats.sortedReasons.length > 0 ? (
                    kpiStats.sortedReasons.map((item, idx) => {
                      const colors = ["text-primary", "text-info", "text-warning", "text-danger", "text-purple", "text-secondary"];
                      return (
                        <div className="mb-1" key={idx}>
                          <span className={`${colors[idx % colors.length]} font-weight-bold`}>●</span> {item.name}: <strong>{item.pct}%</strong> ({item.count})
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-muted small"><T id="shopee.workspace.returns.no-reason-data" /></div>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* Widget 2: Top Returned Products */}
        <Col xs="12" md="6" lg="3" className="mb-3 mb-lg-0">
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 10 }}>
            <CardBody className="p-3">
              <h6 className="font-weight-bold text-dark mb-3" style={{ fontSize: 14 }}>
                <T id="shopee.workspace.returns.top-returned" />
              </h6>
              <div className="small">
                {kpiStats.topProducts.length > 0 ? (
                  kpiStats.topProducts.map((prod, idx) => (
                    <div className="d-flex justify-content-between align-items-center mb-2 pb-1 border-bottom" key={idx}>
                      <div className="d-flex align-items-center">
                        <span className="mr-2">📦</span>
                        <div>
                          <div className="font-weight-bold text-dark">{prod.name}</div>
                          <div className="text-muted" style={{ fontSize: 10 }}>{prod.sku || "—"}</div>
                        </div>
                      </div>
                      <div className="font-weight-bold text-dark">{prod.count} <span className="text-muted"><T id="shopee.workspace.returns.times-suffix" /></span></div>
                    </div>
                  ))
                ) : (
                  <div className="text-muted small py-3 text-center"><T id="shopee.workspace.returns.no-returned-products" /></div>
                )}
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* Widget 3: Refund Amount Trend Line */}
        <Col xs="12" md="6" lg="3" className="mb-3 mb-lg-0">
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 10 }}>
            <CardBody className="p-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="font-weight-bold text-dark mb-0" style={{ fontSize: 14 }}>
                  <T id="shopee.workspace.returns.refund-trend" />
                </h6>
                <Input type="select" bsSize="sm" style={{ width: 90, fontSize: 11 }} defaultValue="7days">
                  <option value="7days"><T id="shopee.workspace.common.last-7-days" /></option>
                  <option value="30days"><T id="shopee.workspace.common.last-30-days" /></option>
                </Input>
              </div>

              {/* Chart line simulation with SVG */}
              <div className="mt-3">
                <svg viewBox="0 0 300 100" style={{ width: "100%", height: 85 }}>
                  <defs>
                    <linearGradient id="refundGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0066FE" stopOpacity="0.3" />
                      <stop offset="100%" stopColor="#0066FE" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path d="M0,80 Q30,60 60,75 T120,40 T180,20 T240,65 T300,70 L300,100 L0,100 Z" fill="url(#refundGrad)" />
                  <path d="M0,80 Q30,60 60,75 T120,40 T180,20 T240,65 T300,70" fill="none" stroke="#0066FE" strokeWidth="3" />
                  <circle cx="180" cy="20" r="4" fill="#0066FE" stroke="#fff" strokeWidth="2" />
                </svg>
                <div className="d-flex justify-content-between small text-muted mt-1" style={{ fontSize: 10 }}>
                  <span>16/07</span>
                  <span>17/07</span>
                  <span>18/07</span>
                  <span>19/07</span>
                  <span>20/07</span>
                  <span>21/07</span>
                  <span>22/07</span>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* Widget 4: Processing Performance */}
        <Col xs="12" md="6" lg="3">
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 10 }}>
            <CardBody className="p-3">
              <h6 className="font-weight-bold text-dark mb-3" style={{ fontSize: 14 }}>
                <T id="shopee.workspace.returns.performance" />
              </h6>
              <div className="p-2 mb-3 rounded d-flex align-items-center" style={{ backgroundColor: "#f8fafc" }}>
                <div className="rounded-circle p-2 mr-3 bg-white shadow-sm text-primary">
                  ⏱️
                </div>
                <div>
                  <div className="small text-muted"><T id="shopee.workspace.returns.avg-processing-time" /></div>
                  <div className="d-flex align-items-center">
                    <span className="font-weight-bold text-dark mr-2" style={{ fontSize: 18 }}><T id="shopee.workspace.returns.sample-processing-time" /></span>
                    <span className="small text-success font-weight-bold"><T id="shopee.workspace.returns.sample-processing-delta" /></span>
                  </div>
                </div>
              </div>

              <div className="p-2 rounded d-flex align-items-center" style={{ backgroundColor: "#f8fafc" }}>
                <div className="rounded-circle p-2 mr-3 bg-white shadow-sm text-success">
                  %
                </div>
                <div>
                  <div className="small text-muted"><T id="shopee.workspace.returns.on-time-rate" /></div>
                  <div className="d-flex align-items-center">
                    <span className="font-weight-bold text-dark mr-2" style={{ fontSize: 18 }}>96.2%</span>
                    <span className="small text-success font-weight-bold">+2.5%</span>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* 8. Original Inspection / Dispute Modal */}
      <Modal isOpen={Boolean(dialog)} toggle={() => setDialog(null)}>
        <ModalHeader toggle={() => setDialog(null)}>
          {dialog && dialog.action === "dispute" ? (
            <T id="shopee.workspace.returns.dispute-modal-title" />
          ) : (
            <T id="shopee.workspace.returns.inspection-modal-title" />
          )}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label><T id="shopee.workspace.return-reason" /></Label>
            <Input type="textarea" value={reason} onChange={event => setReason(event.target.value)} placeholder={intl.formatMessage({ id: "shopee.workspace.returns.condition-placeholder" })} />
          </FormGroup>

          {dialog && dialog.action === "confirm" ? (
            <FormGroup>
              <Label className="font-weight-bold"><T id="shopee.workspace.returns.stock-decision" /></Label>
              <Input type="select" value={stockDecision} onChange={e => setStockDecision(e.target.value)}>
                <option value="RESELLABLE"><T id="shopee.workspace.returns.stock.resellable" /></option>
                <option value="DEFECTIVE"><T id="shopee.workspace.returns.stock.defective" /></option>
                <option value="DISCARD"><T id="shopee.workspace.returns.stock.discard" /></option>
                <option value="SHORTAGE"><T id="shopee.workspace.returns.stock.shortage" /></option>
              </Input>
            </FormGroup>
          ) : null}

          {dialog && dialog.action === "dispute" ? (
            <FormGroup>
              <Label><T id="shopee.workspace.return-evidence" /></Label>
              <Input
                value={evidence}
                onChange={event => setEvidence(event.target.value)}
                placeholder={intl.formatMessage({ id: "shopee.workspace.returns.evidence-placeholder" })}
              />
            </FormGroup>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDialog(null)}><T id="shopee.cancel" /></Button>
          <Button
            color={dialog && dialog.action === "dispute" ? "danger" : "success"}
            disabled={!reason.trim() || (dialog && dialog.action === "dispute" && !evidence.trim())}
            onClick={submitOriginalDialog}
          >
            <T id="shopee.workspace.confirm" />
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
});

export function FinancePanel() {
  return <ShopeeFinancePanel />;
}

export const AutomationPanel = injectIntl(function AutomationPanel({
  intl
}) {
  const workspace = useContext(ShopeeWorkspaceContext);
  const [form, setForm] = useState(null);
  useEffect(() => setForm(workspace.data.settings), [workspace.data.settings]);
  if (workspace.selectedShopId === "ALL") {
    return (
      <Card className="mb-3">
        <CardBody>
          <h5><T id="shopee.workspace.automation" /></h5>
          <Alert color="info" className="mb-0">
            <T id="shopee.workspace.select-shop-for-automation" />
          </Alert>
        </CardBody>
      </Card>
    );
  }
  if (!form) return <Empty />;
  const writable = canWrite(workspace.capabilities, "shopee_shop");
  const toggle = key => setForm(current => ({ ...current, [key]: !current[key] }));
  return (
    <Card className="mb-3">
      <CardBody>
        <h5><T id="shopee.workspace.automation" /></h5>
        {[
          "auto_sync_orders",
          "auto_create_bill",
          "auto_sync_inventory",
          "auto_sync_catalog",
          "notify_new_order",
          "notify_status_change",
          "notify_return"
        ].map(key => (
          <FormGroup check className="mb-3" key={key}>
            <Label check>
              <Input type="checkbox" checked={Boolean(form[key])} onChange={() => toggle(key)} />{" "}
              <T id={`shopee.workspace.setting.${key}`} />
            </Label>
          </FormGroup>
        ))}
        <FormGroup>
          <Label><T id="shopee.workspace.sync-interval" /></Label>
          <Input
            type="number"
            min="5"
            max="1440"
            value={form.sync_interval_minutes || 5}
            onChange={event => setForm({ ...form, sync_interval_minutes: Number(event.target.value) })}
          />
        </FormGroup>
        <FormGroup>
          <Label><T id="shopee.workspace.catalog-sync-interval" /></Label>
          <Input
            type="number"
            min="60"
            max="10080"
            step="60"
            disabled={!form.auto_sync_catalog}
            value={form.catalog_sync_interval_minutes || 1440}
            onChange={event => setForm({
              ...form,
              catalog_sync_interval_minutes: Number(event.target.value)
            })}
          />
          <small className="text-muted">
            <T id="shopee.workspace.catalog-sync-hint" />
          </small>
        </FormGroup>
        <Row className="mb-3">
          <Col md="6">
            <small className="text-muted d-block">
              <T id="shopee.workspace.catalog-last-sync" />
            </small>
            <span>{form.catalog_last_synced_at ? new Date(form.catalog_last_synced_at).toLocaleString() : "—"}</span>
          </Col>
          <Col md="6">
            <small className="text-muted d-block">
              <T id="shopee.workspace.catalog-next-sync" />
            </small>
            <span>{form.catalog_next_sync_at ? new Date(form.catalog_next_sync_at).toLocaleString() : "—"}</span>
          </Col>
        </Row>
        <FormGroup>
          <Label><T id="shopee.workspace.inventory-safety-buffer" /></Label>
          <Input
            type="number"
            min="0"
            value={form.inventory_safety_buffer || 0}
            onChange={event =>
              setForm({
                ...form,
                inventory_safety_buffer: Math.max(
                  0,
                  Number(event.target.value)
                )
              })
            }
          />
        </FormGroup>
        {writable ? <Button color="primary" disabled={workspace.busy["save-settings"]} onClick={() => workspace.runAction(
          "save-settings",
          key => ShopeeV2Api.saveSettings({ ...form, shop_id: workspace.selectedShopId }, key),
          intl.formatMessage({ id: "shopee.workspace.automation.settings-saved" })
        )}><T id="shopee.workspace.save" /></Button> : null}
      </CardBody>
    </Card>
  );
});

export const LogsPanel = injectIntl(function LogsPanel({ intl }) {
  const workspace = useContext(ShopeeWorkspaceContext);
  const writable = canWrite(workspace.capabilities, "shopee_monitor");
  return (
    <Card className="mb-3">
      <CardBody>
        <h5><T id="shopee.workspace.logs" /></h5>
        <TableShell
          rowKey="id"
          rows={workspace.data.jobs}
          columns={[
            { key: "resource", label: <T id="shopee.workspace.resource" /> },
            { key: "action", label: <T id="shopee.workspace.actions" /> },
            { key: "status", label: <T id="shopee.workspace.state" />, render: value => <Status value={value} /> },
            { key: "attempts", label: <T id="shopee.workspace.attempts" /> },
            { key: "createdAt", label: <T id="shopee.workspace.created-at" />, render: value => value ? new Date(value).toLocaleString() : "—" }
          ]}
          actions={writable ? row =>
            ["failed", "dead"].includes(row.status) ? (
              <Button size="sm" color="warning" onClick={() => workspace.runAction(
                `replay-${row.id}`,
                key => ShopeeV2Api.replayJob(row.id, key),
                intl.formatMessage({ id: "shopee.workspace.logs.replayed" })
              )}><T id="shopee.workspace.retry" /></Button>
            ) : <span className="text-muted">—</span> : null}
        />
      </CardBody>
    </Card>
  );
})
